import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Menu, X, ChevronDown, User, LogOut, Building2, Calendar, LayoutDashboard } from 'lucide-react';
import { HiUserGroup, HiOfficeBuilding } from 'react-icons/hi';

const LOGO_URL = '/logo.png';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showHowToMenu, setShowHowToMenu] = useState(false);
  const [showSignInMenu, setShowSignInMenu] = useState(false);
  const howToRef = useRef(null);
  const signInRef = useRef(null);
  const { isAuthenticated, user, logout, isCommunityMember, isBusinessOwner, isAdmin } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (howToRef.current && !howToRef.current.contains(e.target)) setShowHowToMenu(false);
      if (signInRef.current && !signInRef.current.contains(e.target)) setShowSignInMenu(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/');
    setShowUserMenu(false);
  };

  const getDashboardLink = () => {
    if (isAdmin) return '/admin';
    if (isBusinessOwner) return '/business-account';
    return '/community-account';
  };

  return (
    <nav className="sticky top-0 z-50 bg-white border-b border-neutral-200 shadow-sm" data-testid="navbar">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex-shrink-0" data-testid="logo-link">
            <img 
              src={LOGO_URL} 
              alt="Explore MHC" 
              style={{ height: '56px', width: 'auto', maxWidth: '200px', objectFit: 'contain' }}
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            <Link 
              to="/" 
              className="text-neutral-700 hover:text-primary font-medium transition-colors"
              data-testid="nav-home"
            >
              Home
            </Link>
            <Link 
              to="/businesses" 
              className="text-neutral-700 hover:text-primary font-medium transition-colors"
              data-testid="nav-businesses"
            >
              Business Directory
            </Link>
            <Link 
              to="/minority-owned" 
              className="text-neutral-700 hover:text-primary font-medium transition-colors"
              data-testid="nav-minority"
            >
              Minority-Owned
            </Link>
            <Link 
              to="/events" 
              className="text-neutral-700 hover:text-primary font-medium transition-colors"
              data-testid="nav-events"
            >
              Events
            </Link>
            <Link 
              to="/contact" 
              className="text-neutral-700 hover:text-primary font-medium transition-colors"
              data-testid="nav-contact"
            >
              Contact Us
            </Link>
            <Link 
              to="/pricing" 
              className="text-neutral-700 hover:text-primary font-medium transition-colors"
              data-testid="nav-pricing"
            >
              Pricing
            </Link>
            <div className="relative" ref={howToRef}>
              <button 
                onClick={() => setShowHowToMenu(!showHowToMenu)}
                className="flex items-center gap-1 text-neutral-700 hover:text-primary font-medium transition-colors"
                data-testid="nav-how-to"
              >
                How To <ChevronDown className={`w-4 h-4 transition-transform ${showHowToMenu ? 'rotate-180' : ''}`} />
              </button>
              {showHowToMenu && (
                <div className="absolute top-full right-0 mt-2 w-52 bg-white rounded-xl shadow-lg border border-neutral-100 py-2 z-50">
                  <Link to="/how-to" onClick={() => setShowHowToMenu(false)}
                    className="block px-4 py-2.5 text-sm text-neutral-700 hover:bg-neutral-50 hover:text-primary font-medium" data-testid="nav-howto-overview">
                    Overview
                  </Link>
                  <Link to="/how-to#add-business" onClick={() => setShowHowToMenu(false)}
                    className="flex items-center gap-2 px-4 py-2.5 text-sm text-neutral-700 hover:bg-neutral-50 hover:text-primary" data-testid="nav-howto-business">
                    <Building2 className="w-4 h-4" /> Add a Business
                  </Link>
                  <Link to="/how-to#submit-event" onClick={() => setShowHowToMenu(false)}
                    className="flex items-center gap-2 px-4 py-2.5 text-sm text-neutral-700 hover:bg-neutral-50 hover:text-primary" data-testid="nav-howto-event">
                    <Calendar className="w-4 h-4" /> Submit an Event
                  </Link>
                </div>
              )}
            </div>
          </div>

          {/* Auth Buttons / User Menu */}
          <div className="hidden lg:flex items-center gap-4">
            {isAuthenticated ? (
              <div className="relative">
                <button
                  onClick={() => setShowUserMenu(!showUserMenu)}
                  className="flex items-center gap-2 px-4 py-2 rounded-full bg-neutral-100 hover:bg-neutral-200 transition-colors"
                  data-testid="user-menu-button"
                >
                  <div className="w-8 h-8 rounded-full bg-primary text-white flex items-center justify-center font-semibold text-sm">
                    {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                  </div>
                  <span className="font-medium text-neutral-700">{user?.name}</span>
                  <ChevronDown className="w-4 h-4 text-neutral-500" />
                </button>

                {showUserMenu && (
                  <div className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-lg border border-neutral-200 py-2 animate-fade-in">
                    <Link
                      to={getDashboardLink()}
                      className="flex items-center gap-3 px-4 py-3 hover:bg-neutral-50 text-neutral-700"
                      onClick={() => setShowUserMenu(false)}
                      data-testid="user-menu-dashboard"
                    >
                      <LayoutDashboard className="w-5 h-5" />
                      Dashboard
                    </Link>
                    {isBusinessOwner && (
                      <Link
                        to="/submit-business"
                        className="flex items-center gap-3 px-4 py-3 hover:bg-neutral-50 text-neutral-700"
                        onClick={() => setShowUserMenu(false)}
                        data-testid="user-menu-submit-business"
                      >
                        <Building2 className="w-5 h-5" />
                        Submit Business
                      </Link>
                    )}
                    {(isCommunityMember || isBusinessOwner) && (
                      <Link
                        to="/submit-event"
                        className="flex items-center gap-3 px-4 py-3 hover:bg-neutral-50 text-neutral-700"
                        onClick={() => setShowUserMenu(false)}
                        data-testid="user-menu-submit-event"
                      >
                        <Calendar className="w-5 h-5" />
                        Submit Event
                      </Link>
                    )}
                    <hr className="my-2 border-neutral-200" />
                    <button
                      onClick={handleLogout}
                      className="flex items-center gap-3 px-4 py-3 hover:bg-neutral-50 text-red-600 w-full"
                      data-testid="user-menu-logout"
                    >
                      <LogOut className="w-5 h-5" />
                      Log Out
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <>
                <div className="relative" ref={signInRef}>
                  <button
                    onClick={() => setShowSignInMenu(!showSignInMenu)}
                    className="flex items-center gap-1 text-neutral-700 hover:text-primary font-medium transition-colors"
                    data-testid="nav-login"
                  >
                    Sign In
                    <ChevronDown className="w-4 h-4" />
                  </button>
                  {showSignInMenu && (
                    <div className="absolute right-0 top-full mt-2 w-64 bg-white border border-neutral-200 rounded-xl shadow-lg overflow-hidden z-50" data-testid="sign-in-dropdown">
                      <Link
                        to="/community-login"
                        onClick={() => setShowSignInMenu(false)}
                        className="flex items-start gap-3 p-4 hover:bg-primary/5 border-b border-neutral-100"
                        data-testid="nav-login-community"
                      >
                        <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                          <HiUserGroup className="w-5 h-5 text-primary" />
                        </div>
                        <div>
                          <div className="font-semibold text-neutral-900">Community Member</div>
                          <div className="text-xs text-neutral-500">Discover &amp; submit events</div>
                        </div>
                      </Link>
                      <Link
                        to="/business-login"
                        onClick={() => setShowSignInMenu(false)}
                        className="flex items-start gap-3 p-4 hover:bg-secondary/5"
                        data-testid="nav-login-business"
                      >
                        <div className="w-10 h-10 rounded-lg bg-secondary/10 flex items-center justify-center flex-shrink-0">
                          <HiOfficeBuilding className="w-5 h-5 text-secondary" />
                        </div>
                        <div>
                          <div className="font-semibold text-neutral-900">Business Owner</div>
                          <div className="text-xs text-neutral-500">Manage your listing &amp; events</div>
                        </div>
                      </Link>
                    </div>
                  )}
                </div>
                <Link
                  to="/register"
                  className="btn-accent"
                  data-testid="nav-register"
                >
                  Get Started
                </Link>
              </>
            )}
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="lg:hidden p-2 rounded-lg hover:bg-neutral-100"
            data-testid="mobile-menu-button"
          >
            {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Navigation */}
      {isOpen && (
        <div className="lg:hidden bg-white border-t border-neutral-200 animate-slide-up">
          <div className="container mx-auto px-4 py-4 space-y-2">
            <Link
              to="/"
              className="block px-4 py-3 rounded-lg hover:bg-neutral-100 font-medium"
              onClick={() => setIsOpen(false)}
            >
              Home
            </Link>
            <Link
              to="/businesses"
              className="block px-4 py-3 rounded-lg hover:bg-neutral-100 font-medium"
              onClick={() => setIsOpen(false)}
            >
              Business Directory
            </Link>
            <Link
              to="/minority-owned"
              className="block px-4 py-3 rounded-lg hover:bg-neutral-100 font-medium"
              onClick={() => setIsOpen(false)}
            >
              Minority-Owned
            </Link>
            <Link
              to="/events"
              className="block px-4 py-3 rounded-lg hover:bg-neutral-100 font-medium"
              onClick={() => setIsOpen(false)}
            >
              Events
            </Link>
            <Link
              to="/contact"
              className="block px-4 py-3 rounded-lg hover:bg-neutral-100 font-medium"
              onClick={() => setIsOpen(false)}
            >
              Contact Us
            </Link>
            <Link
              to="/pricing"
              className="block px-4 py-3 rounded-lg hover:bg-neutral-100 font-medium"
              onClick={() => setIsOpen(false)}
            >
              Pricing
            </Link>
            <Link
              to="/how-to"
              className="block px-4 py-3 rounded-lg hover:bg-neutral-100 font-medium"
              onClick={() => setIsOpen(false)}
            >
              How To
            </Link>
            <Link
              to="/how-to#add-business"
              className="block px-8 py-2 rounded-lg hover:bg-neutral-100 text-sm text-neutral-600"
              onClick={() => setIsOpen(false)}
            >
              Add a Business
            </Link>
            <Link
              to="/how-to#submit-event"
              className="block px-8 py-2 rounded-lg hover:bg-neutral-100 text-sm text-neutral-600"
              onClick={() => setIsOpen(false)}
            >
              Submit an Event
            </Link>
            
            <hr className="my-4 border-neutral-200" />
            
            {isAuthenticated ? (
              <>
                <Link
                  to={getDashboardLink()}
                  className="block px-4 py-3 rounded-lg bg-neutral-100 font-medium"
                  onClick={() => setIsOpen(false)}
                >
                  Dashboard
                </Link>
                <button
                  onClick={() => { handleLogout(); setIsOpen(false); }}
                  className="block w-full text-left px-4 py-3 rounded-lg hover:bg-red-50 text-red-600 font-medium"
                >
                  Log Out
                </button>
              </>
            ) : (
              <>
                <Link
                  to="/community-login"
                  className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-primary/5 font-medium border border-neutral-200"
                  onClick={() => setIsOpen(false)}
                  data-testid="mobile-nav-login-community"
                >
                  <HiUserGroup className="w-5 h-5 text-primary" />
                  <span>Community Member Login</span>
                </Link>
                <Link
                  to="/business-login"
                  className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-secondary/5 font-medium border border-neutral-200"
                  onClick={() => setIsOpen(false)}
                  data-testid="mobile-nav-login-business"
                >
                  <HiOfficeBuilding className="w-5 h-5 text-secondary" />
                  <span>Business Owner Login</span>
                </Link>
                <Link
                  to="/register"
                  className="block px-4 py-3 rounded-lg bg-accent text-neutral-900 font-bold text-center"
                  onClick={() => setIsOpen(false)}
                >
                  Get Started
                </Link>
              </>
            )}
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
